/**
 * AiLimitsTab — AI tools > Limits & access admin view (Phase X.3.7c C1b).
 *
 * Per-CM daily/weekly AI-invocation usage vs effective caps, with inline
 * override editing. The effective cap resolves override -> tenant default ->
 * hardcoded fallback (server-side); a "source" chip shows which tier applies.
 * Owners are exempt (no cap, not editable). Owner/admin only (the GET/PUT
 * endpoints enforce manage_team; the rail visibility mirrors that).
 *
 * Caps are SOFT throttles: counts come from ai_invocations (the #234 Usage
 * source), and the per-call guard fails open, so this view is informational +
 * a control surface, not a hard gate.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiCapsApi,
  addDaysIso,
  formatEndsOn,
  DEFAULT_OVERRIDE_DAYS,
  MAX_OVERRIDE_DAYS,
  type CmCapAdminRow,
  type CmCapsResponse,
  type CmCapSource,
} from '@/lib/aiCaps';
import { aiUsageApi, estimateCostUsd, type UsageReport } from '@/lib/aiUsage';
import {
  aiToolsEnabledQueryKey,
  toolTogglesApi,
  TOOL_LABELS,
  type ToolKey,
  type ToolTogglesResponse,
} from '@/lib/aiToolToggles';
import { formatRecordCount } from '@/lib/format';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { DateInput } from './primitives/DateInput';

export interface AiLimitsTabProps {
  tenantSlug: string;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const SOURCE_LABEL: Record<CmCapSource, string> = {
  override: 'Set here',
  tenant: 'Tenant default',
  fallback: 'Default',
};

function personLabel(row: CmCapAdminRow): string {
  if (row.display_name && row.display_name.trim().length > 0) return row.display_name.trim();
  if (row.email && row.email.trim().length > 0) return row.email.trim();
  return `${row.user_id.slice(0, 8)}…`;
}

/**
 * How close a person's usage sits to their cap: 'at' from the cap (or a cap
 * of 0), 'near' from 80%, null below. The gate itself refuses one call past
 * the cap (a soft throttle), so 'at' means the next call is the last. Integer
 * arithmetic, so the threshold never depends on how a decimal rounds: 28 of
 * 35 is exactly 80%, whatever 0.8 * 35 comes to.
 */
export type CapLevel = 'near' | 'at';
export function capLevel(used: number, cap: number): CapLevel | null {
  if (cap <= 0 || used >= cap) return 'at';
  if (used * 5 >= cap * 4) return 'near';
  return null;
}

/** The colour a marked cell wears: the page's warning and error tokens. */
function levelClass(level: CapLevel | null): string {
  if (level === 'at') return 'text-danger';
  if (level === 'near') return 'text-caution-deep';
  return 'text-ink-soft';
}

/** '' -> inherit (null); a non-negative integer -> set; anything else invalid. */
function parseCapInput(s: string): { ok: true; value: number | null } | { ok: false } {
  const t = s.trim();
  if (t === '') return { ok: true, value: null };
  const n = Number(t);
  if (Number.isInteger(n) && n >= 0) return { ok: true, value: n };
  return { ok: false };
}

/**
 * The Source cell, keyed on the own limit's status (not on daily_source, so
 * a weekly-only limit reads "Set here" too). An ended limit reads as the
 * inherited source it fell back to, with the day it ended, so it never
 * looks lowered by hand.
 */
function sourceCell(row: CmCapAdminRow): ReactNode {
  if (row.exempt) return '–';
  const inherited = SOURCE_LABEL[row.daily_source];
  switch (row.override_status) {
    case 'in_effect':
      return row.ends_on === null ? SOURCE_LABEL.override : `${SOURCE_LABEL.override} · through ${formatEndsOn(row.ends_on)}`;
    case 'no_end_date':
      return (
        <>
          {SOURCE_LABEL.override} · <span className="text-caution-deep">no end date</span>
        </>
      );
    case 'ended':
      return row.ends_on === null ? inherited : `${inherited} · own limit ended ${formatEndsOn(row.ends_on)}`;
    default:
      return inherited;
  }
}

/** A YYYY-MM-DD real calendar day from `min` to `max`, inclusive. */
function endsOnInRange(value: string, min: string, max: string | null): boolean {
  if (addDaysIso(value, 0) !== value) return false;
  return max !== null && value >= min && value <= max;
}

export function AiLimitsTab({ tenantSlug }: AiLimitsTabProps): ReactNode {
  const queryClient = useQueryClient();
  const query = useQuery<CmCapsResponse, Error>({
    queryKey: ['admin-review', 'ai-caps', tenantSlug],
    queryFn: ({ signal }) => aiCapsApi.list(tenantSlug, signal),
    staleTime: 30_000,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftDaily, setDraftDaily] = useState('');
  const [draftWeekly, setDraftWeekly] = useState('');
  const [draftEndsOn, setDraftEndsOn] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (args: {
      userId: string;
      daily_cap: number | null;
      weekly_cap: number | null;
      ends_on: string | null;
    }) =>
      aiCapsApi.setCap(tenantSlug, args.userId, {
        daily_cap: args.daily_cap,
        weekly_cap: args.weekly_cap,
        ends_on: args.ends_on,
      }),
    onSuccess: async () => {
      setToast('Caps saved');
      setEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-review', 'ai-caps', tenantSlug] });
    },
  });

  const rows = query.data?.rows ?? [];
  const fallback = query.data?.fallback;
  const tenantDefaults = query.data?.tenant_defaults;
  const readCaps = query.data?.read_caps;
  // The date box runs from the practice's today, as the server sent it, to
  // MAX_OVERRIDE_DAYS after it: never the viewer's own clock.
  const today = query.data?.today ?? '';
  const maxEndsOn = today === '' ? null : addDaysIso(today, MAX_OVERRIDE_DAYS);

  const defaultsNote = useMemo(() => {
    if (!fallback) return '';
    const d = tenantDefaults?.daily_cap ?? fallback.daily_cap;
    const w = tenantDefaults?.weekly_cap ?? fallback.weekly_cap;
    const reads = readCaps
      ? ` Note reads are counted separately: ${formatRecordCount(readCaps.daily_cap)}/day, ${formatRecordCount(readCaps.weekly_cap)}/week for everyone.`
      : '';
    return `Inherited caps: ${d}/day, ${w}/week. Leave a field blank to inherit.${reads}`;
  }, [fallback, tenantDefaults, readCaps]);

  function startEdit(row: CmCapAdminRow): void {
    setEditingId(row.user_id);
    setDraftDaily(row.daily_cap_override === null ? '' : String(row.daily_cap_override));
    setDraftWeekly(row.weekly_cap_override === null ? '' : String(row.weekly_cap_override));
    // A limit in effect keeps its own last day; a new, ended or undated one
    // starts DEFAULT_OVERRIDE_DAYS after today.
    setDraftEndsOn(
      row.override_status === 'in_effect' && row.ends_on !== null
        ? row.ends_on
        : (today === '' ? '' : (addDaysIso(today, DEFAULT_OVERRIDE_DAYS) ?? '')),
    );
    setFormError(null);
  }

  function save(userId: string): void {
    const d = parseCapInput(draftDaily);
    const w = parseCapInput(draftWeekly);
    if (!d.ok || !w.ok) {
      setFormError('Enter a whole number, 0 or more, or leave it blank to use the default.');
      return;
    }
    // Both blank clears the limit: the date is not read.
    if (d.value === null && w.value === null) {
      mutation.mutate({ userId, daily_cap: null, weekly_cap: null, ends_on: null });
      return;
    }
    const endsOn = draftEndsOn.trim();
    if (!endsOnInRange(endsOn, today, maxEndsOn)) {
      setFormError('Pick the last day this limit applies, from today to a year ahead.');
      return;
    }
    mutation.mutate({ userId, daily_cap: d.value, weekly_cap: w.value, ends_on: endsOn });
  }

  return (
    <div>
      <ToolTogglesSection tenantSlug={tenantSlug} />

      <TokensTodayLine tenantSlug={tenantSlug} />

      <div className="mb-4">
        <h3 className="font-display text-lg text-sage-800">Per-person AI limits</h3>
        <p className="text-xs text-ink-soft mt-1 max-w-prose">
          Owners are exempt. {defaultsNote}
        </p>
      </div>

      {toast && (
        <div
          role="status"
          className="mb-3 px-3 py-2 text-sm bg-sage-50 border border-sage-200 text-sage-800 rounded-md"
        >
          {toast}
        </div>
      )}

      {query.isLoading && (
        <div>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="the AI limits" />}

      {query.isSuccess && rows.length === 0 && (
        <div className="px-3 py-8 text-center bg-sage-50 border border-sage-100 rounded-card text-sm text-ink">
          Add team members on the People page and you'll see them here.
        </div>
      )}

      {query.isSuccess && rows.length > 0 && (
        <div className="overflow-x-auto bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-sage-800/[8%] text-left text-xs text-ink-soft">
                <th className="px-3 py-2 font-medium">Person</th>
                <th className="px-3 py-2 font-medium text-right whitespace-nowrap">Today</th>
                <th className="px-3 py-2 font-medium text-right whitespace-nowrap">This week</th>
                <th className="px-3 py-2 font-medium text-right whitespace-nowrap">Reads today</th>
                <th className="px-3 py-2 font-medium text-right whitespace-nowrap">Blocked</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium text-right">Limits</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isEditing = editingId === row.user_id;
                const dailyLevel = row.exempt ? null : capLevel(row.daily_used, row.daily_cap_effective);
                const weeklyLevel = row.exempt ? null : capLevel(row.weekly_used, row.weekly_cap_effective);
                return (
                  <tr key={row.user_id} className="border-b border-sage-800/[5%] last:border-0 align-top">
                    <td className="px-3 py-2 text-ink">
                      {personLabel(row)}
                      {row.exempt && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-sage-700 bg-sage-100 rounded-pill px-1.5 py-0.5">
                          Exempt
                        </span>
                      )}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${levelClass(dailyLevel)}`}
                      data-cap-level={dailyLevel ?? undefined}
                    >
                      {row.exempt
                        ? formatRecordCount(row.daily_used)
                        : `${formatRecordCount(row.daily_used)} / ${formatRecordCount(row.daily_cap_effective)}`}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${levelClass(weeklyLevel)}`}
                      data-cap-level={weeklyLevel ?? undefined}
                    >
                      {row.exempt
                        ? formatRecordCount(row.weekly_used)
                        : `${formatRecordCount(row.weekly_used)} / ${formatRecordCount(row.weekly_cap_effective)}`}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-soft whitespace-nowrap">
                      {row.exempt || !readCaps
                        ? formatRecordCount(row.daily_read_used)
                        : `${formatRecordCount(row.daily_read_used)} / ${formatRecordCount(readCaps.daily_cap)}`}
                    </td>
                    <td
                      className="px-3 py-2 text-right tabular-nums text-ink-soft whitespace-nowrap"
                      data-testid={`blocked-${row.user_id}`}
                    >
                      {row.daily_blocks === 0 && row.weekly_blocks === 0
                        ? '–'
                        : `${formatRecordCount(row.daily_blocks)} today · ${formatRecordCount(row.weekly_blocks)} this week`}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-soft whitespace-nowrap">{sourceCell(row)}</td>
                    <td className="px-3 py-2 text-right">
                      {row.exempt ? (
                        <span className="text-xs text-ink-soft">No limit</span>
                      ) : isEditing ? (
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1.5">
                            <label className="sr-only" htmlFor={`daily-${row.user_id}`}>
                              Daily cap for {personLabel(row)}
                            </label>
                            <input
                              id={`daily-${row.user_id}`}
                              type="number"
                              min={0}
                              inputMode="numeric"
                              placeholder="day"
                              value={draftDaily}
                              onChange={(e) => setDraftDaily(e.target.value)}
                              className="w-16 text-right rounded-base border border-sage-800/[12%] px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                            />
                            <span className="text-xs text-ink-soft">/</span>
                            <label className="sr-only" htmlFor={`weekly-${row.user_id}`}>
                              Weekly cap for {personLabel(row)}
                            </label>
                            <input
                              id={`weekly-${row.user_id}`}
                              type="number"
                              min={0}
                              inputMode="numeric"
                              placeholder="wk"
                              value={draftWeekly}
                              onChange={(e) => setDraftWeekly(e.target.value)}
                              className="w-16 text-right rounded-base border border-sage-800/[12%] px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                            />
                          </div>
                          <label className="sr-only" htmlFor={`ends-on-${row.user_id}`}>
                            Last day this limit applies for {personLabel(row)}
                          </label>
                          <DateInput
                            id={`ends-on-${row.user_id}`}
                            data-testid={`ends-on-${row.user_id}`}
                            size="compact"
                            min={today || undefined}
                            max={maxEndsOn ?? undefined}
                            value={draftEndsOn}
                            onChange={(e) => setDraftEndsOn(e.target.value)}
                          />
                          {formError && <span className="text-xs text-pepper">{formError}</span>}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => save(row.user_id)}
                              disabled={mutation.isPending}
                              className="min-h-9 px-3 py-1 text-xs font-medium text-cream bg-sage-700 rounded-md hover:bg-sage-800 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                            >
                              {mutation.isPending ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="min-h-9 px-3 py-1 text-xs font-medium text-ink-soft border border-sage-800/[12%] rounded-md hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(row)}
                          className="min-h-9 px-3 py-1 text-xs font-medium text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                        >
                          {row.override_status !== 'none' ? 'Edit' : 'Set limit'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {mutation.isError && (
        <div className="mt-3">
          <ErrorBanner error={mutation.error} kind="write" />
        </div>
      )}
    </div>
  );
}

/**
 * Today's tenant-wide token total against the daily budget: the number the
 * budget guard enforces, read from the same usage report the Usage tab shows
 * (the 7-day window; only the day figures are used here). The dollar figure
 * is the Usage tab's flat blended estimate, labelled as such.
 */
function TokensTodayLine({ tenantSlug }: { tenantSlug: string }): ReactNode {
  const query = useQuery<UsageReport, Error>({
    queryKey: ['admin-review', 'ai-usage', tenantSlug, 7],
    queryFn: ({ signal }) => aiUsageApi.report(tenantSlug, 7, signal),
    staleTime: 30_000,
  });
  if (!query.isSuccess) return null;
  const tokens = query.data.tenant_tokens_today;
  const budget = query.data.tenant_daily_token_budget;
  const against =
    budget === null
      ? `Today: ${formatRecordCount(tokens)} tokens · no daily budget set`
      : `Today: ${formatRecordCount(tokens)} tokens of ${formatRecordCount(budget)}`;
  return (
    <p className="mb-4 text-sm text-ink" data-testid="ai-tokens-today">
      {against}
      <span className="text-xs text-ink-soft">
        {' '}
        (about {USD.format(estimateCostUsd(tokens))}, a flat-rate estimate, not a billed amount)
      </span>
    </p>
  );
}

/**
 * Per-tenant tool on/off toggles (X.3.7c C2). Turning a tool off hides it in the
 * portal and blocks it server-side (403 tool_disabled). Self-contained query +
 * mutation so the caps surface above is untouched.
 */
function ToolTogglesSection({ tenantSlug }: { tenantSlug: string }): ReactNode {
  const queryClient = useQueryClient();
  const query = useQuery<ToolTogglesResponse, Error>({
    queryKey: ['admin-review', 'tool-toggles', tenantSlug],
    queryFn: ({ signal }) => toolTogglesApi.list(tenantSlug, signal),
    staleTime: 30_000,
  });
  const [toast, setToast] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (args: { toolKey: ToolKey; enabled: boolean }) =>
      toolTogglesApi.setToggle(tenantSlug, args.toolKey, args.enabled),
    onSuccess: async (_data, args) => {
      setToast(`${TOOL_LABELS[args.toolKey]} ${args.enabled ? 'turned on' : 'turned off'}`);
      // The admin list, and the portal read the landing and the photo doors
      // share, so this admin's own screens follow the switch at once.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-review', 'tool-toggles', tenantSlug] }),
        queryClient.invalidateQueries({ queryKey: aiToolsEnabledQueryKey(tenantSlug) }),
      ]);
    },
  });

  const tools = query.data?.tools ?? [];

  return (
    <section className="mb-6">
      <h3 className="font-display text-lg text-sage-800">Available tools</h3>
      <p className="text-xs text-ink-soft mt-1 mb-2 max-w-prose">
        Turn an AI tool off for everyone. An off tool disappears from the menu.
      </p>

      {toast && (
        <div
          role="status"
          className="mb-3 px-3 py-2 text-sm bg-sage-50 border border-sage-200 text-sage-800 rounded-md"
        >
          {toast}
        </div>
      )}

      {query.isLoading && <SkeletonRow />}
      {query.isError && <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="the AI limits" />}

      {query.isSuccess && (
        <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
          {tools.map((tool) => (
            <div
              key={tool.tool_key}
              className="flex items-center justify-between px-3 py-2 border-b border-sage-800/[5%] last:border-0"
            >
              <span className="text-sm text-ink">{TOOL_LABELS[tool.tool_key]}</span>
              <button
                type="button"
                role="switch"
                aria-checked={tool.enabled}
                aria-label={TOOL_LABELS[tool.tool_key]}
                disabled={mutation.isPending}
                onClick={() => mutation.mutate({ toolKey: tool.tool_key, enabled: !tool.enabled })}
                className={
                  'min-h-9 px-3 py-1 text-xs font-semibold rounded-pill border disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                  (tool.enabled
                    ? 'bg-sage-100 text-sage-700 border-sage-300'
                    : 'bg-warm-50 text-ink-soft border-warm-300')
                }
              >
                {tool.enabled ? 'On' : 'Off'}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
